<template>
  <div
    v-if="blocking"
    class="mobile-web-blocker"
    role="alertdialog"
    aria-modal="true"
    aria-labelledby="mobile-block-title"
  >
    <!-- Mirrors SiteLayout / MenuBar mobile strip -->
    <header class="mobile-web-blocker__header" aria-hidden="true">
      <div class="mobile-web-blocker__nav-row">
        <div class="mobile-web-blocker__logo-wrap">
          <img
            src="@/assets/site/images/govava-logo-new.png"
            alt=""
            class="mobile-web-blocker__logo"
          />
        </div>
        <div class="mobile-web-blocker__menu-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
            <path
              d="M5 12H18"
              stroke="#000000"
              stroke-width="2"
              stroke-linecap="round"
            />
            <path
              d="M5 17H11"
              stroke="#000000"
              stroke-width="2"
              stroke-linecap="round"
            />
            <path
              d="M5 7H15"
              stroke="#000000"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </div>
      </div>
      <div class="mobile-web-blocker__gradient" />
    </header>

    <div class="mobile-web-blocker__body">
      <div class="mobile-web-blocker__panel">
        <p class="gift-text">Find The Perfect Gift <sup>TM</sup></p>
        <h1 id="mobile-block-title" class="mobile-web-blocker__title">
          Please use the <span class="mobile-web-blocker__brand">GOVAVA</span> mobile app
        </h1>
        <p class="mobile-web-blocker__lead">
          Shopping on the web is only supported on a desktop browser. On phones
          and tablets, please install our app to continue.
        </p>

        <app-store-badges
          :show-apple="showAppleStore"
          :show-android="showAndroidStore"
          :links-class="{ 'store-links--single': showSingleStoreBadge }"
        />

        <p class="store-text-links">
          <template v-if="showAppleStore && !showAndroidStore">
            <a :href="APP_STORE_URL" target="_blank" rel="noopener noreferrer"
              >App Store</a
            >
          </template>
          <template v-else-if="showAndroidStore && !showAppleStore">
            <a :href="GOOGLE_PLAY_URL" target="_blank" rel="noopener noreferrer"
              >Google Play</a
            >
          </template>
          <template v-else>
            <a :href="APP_STORE_URL" target="_blank" rel="noopener noreferrer"
              >App Store</a
            >
            <span aria-hidden="true"> · </span>
            <a :href="GOOGLE_PLAY_URL" target="_blank" rel="noopener noreferrer"
              >Google Play</a
            >
          </template>
        </p>

        <button
          v-if="forcePreview"
          type="button"
          class="mobile-web-blocker__preview-exit"
          @click="exitPreview"
        >
          Continue to website (preview only)
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getMobileStorePlatform,
  isClientMobileWebContext,
  isMobileAppGateForced,
} from "@/utils/isMobileDevice";
import { APP_STORE_URL, GOOGLE_PLAY_URL } from "@/constants/appStoreUrls";
import AppStoreBadges from "@/components/AppStoreBadges.vue";

const SCROLL_LOCK_CLASS = "govava-mobile-web-block--scroll-lock";

export default {
  name: "MobileAppGate",
  components: {
    AppStoreBadges,
  },
  data() {
    return {
      blocking: false,
      forcePreview: false,
      storePlatform: "unknown",
      APP_STORE_URL,
      GOOGLE_PLAY_URL,
    };
  },
  computed: {
    showAppleStore() {
      if (this.forcePreview) {
        return true;
      }
      if (this.storePlatform === "apple") {
        return true;
      }
      if (this.storePlatform === "android") {
        return false;
      }
      return true;
    },
    showAndroidStore() {
      if (this.forcePreview) {
        return true;
      }
      if (this.storePlatform === "android") {
        return true;
      }
      if (this.storePlatform === "apple") {
        return false;
      }
      return true;
    },
    showSingleStoreBadge() {
      return (
        (this.showAppleStore && !this.showAndroidStore) ||
        (this.showAndroidStore && !this.showAppleStore)
      );
    },
  },
  mounted() {
    this.scheduleSync();
    this._retryDelays = [0, 100, 400, 1200].map((ms) =>
      setTimeout(() => this.scheduleSync(), ms)
    );
    this._onResize = () => this.scheduleSync();
    this._onPageShow = () => this.scheduleSync();
    window.addEventListener("resize", this._onResize);
    window.addEventListener("orientationchange", this._onResize);
    window.addEventListener("pageshow", this._onPageShow);
    window.addEventListener("load", this._onLoad);
  },
  beforeDestroy() {
    if (this._retryDelays) {
      this._retryDelays.forEach((id) => clearTimeout(id));
    }
    window.removeEventListener("resize", this._onResize);
    window.removeEventListener("orientationchange", this._onResize);
    window.removeEventListener("pageshow", this._onPageShow);
    window.removeEventListener("load", this._onLoad);
    this.setScrollLock(false);
  },
  methods: {
    _onLoad() {
      this.scheduleSync();
    },
    scheduleSync() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.syncState();
          });
        });
      });
    },
    syncState() {
      const force = isMobileAppGateForced();
      const treatAsMobile = isClientMobileWebContext();

      if (!force && !treatAsMobile) {
        this.blocking = false;
        this.forcePreview = false;
        this.setScrollLock(false);
        return;
      }

      if (force) {
        this.forcePreview = true;
      } else {
        this.forcePreview = false;
      }

      this.storePlatform = getMobileStorePlatform();
      this.blocking = true;
      this.setScrollLock(true);
    },
    exitPreview() {
      if (!this.forcePreview) {
        return;
      }
      this.blocking = false;
      this.forcePreview = false;
      this.setScrollLock(false);
      try {
        const u = new URL(window.location.href);
        if (u.searchParams.has("mobileAppGate") || u.searchParams.has("govavaMobileGate")) {
          u.searchParams.delete("mobileAppGate");
          u.searchParams.delete("govavaMobileGate");
          const qs = u.searchParams.toString();
          const next = u.pathname + (qs ? "?" + qs : "") + u.hash;
          window.history.replaceState({}, "", next);
        }
      } catch (e) {
        /* ignore */
      }
    },
    setScrollLock(on) {
      if (typeof document === "undefined" || !document.documentElement) {
        return;
      }
      if (on) {
        document.documentElement.classList.add(SCROLL_LOCK_CLASS);
        document.body.classList.add(SCROLL_LOCK_CLASS);
      } else {
        document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
        document.body.classList.remove(SCROLL_LOCK_CLASS);
      }
    },
  },
};
</script>

<style>
.govava-mobile-web-block--scroll-lock {
  overflow: hidden !important;
  height: 100% !important;
  touch-action: none;
}

.mobile-web-blocker {
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
  touch-action: pan-y;
  background: #fafafa;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
}

/* Top chrome aligned with MenuBar mobile (.navbar .mobile) */
.mobile-web-blocker__header {
  flex-shrink: 0;
  background: #fff;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.06);
}

.mobile-web-blocker__nav-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  min-height: 60px;
}

@media screen and (min-width: 1281px) {
  .mobile-web-blocker__nav-row {
    padding: 0 64px;
  }
}

.mobile-web-blocker__logo-wrap {
  height: 60px;
  display: flex;
  align-items: center;
}

.mobile-web-blocker__logo {
  height: 100%;
  width: auto;
  max-width: 200px;
  object-fit: contain;
  object-position: left center;
}

.mobile-web-blocker__menu-icon {
  flex-shrink: 0;
  cursor: default;
  opacity: 0.85;
}

.mobile-web-blocker__menu-icon svg {
  width: 40px;
  height: 40px;
  display: block;
}

.mobile-web-blocker__gradient {
  height: 12px;
  width: 100%;
  background: linear-gradient(to right, #c97410, #ffd700, #c97410);
}

.mobile-web-blocker__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 20px 16px 32px;
}

.mobile-web-blocker__panel {
  max-width: 420px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  text-align: center;
  padding: 24px 20px 28px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e5e5e5;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.mobile-web-blocker__title {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  color: #2b2b2b;
  line-height: 1.3;
}

.mobile-web-blocker__brand {
  color: #fe3c00;
}

.mobile-web-blocker__lead {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.55;
  color: #555;
  max-width: 34em;
}

/* Match MenuBar .navbar .upper .gift-text */
.mobile-web-blocker .gift-text {
  font-family: "Pacifico", cursive;
  margin: 0;
  text-align: center;
  font-weight: 400;
  font-style: normal;
  font-size: 28px;
  color: #e24616;
}

.mobile-web-blocker .gift-text sup {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji",
    "Segoe UI Symbol";
  position: relative;
  top: -12px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
  color: #3b3b3b;
}

.mobile-web-blocker .store-links {
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  margin-top: 6px;
  max-width: 280px;
}

.mobile-web-blocker .store-badge {
  width: 100%;
}

.mobile-web-blocker .store-links--single .store-badge {
  height: 58px;
}

.mobile-web-blocker .store-text-links {
  margin: 2px 0 0;
  font-size: 0.9rem;
}

.mobile-web-blocker .store-text-links a {
  color: #fe3c00;
  font-weight: 600;
  text-decoration: underline;
}

.mobile-web-blocker .store-text-links a:active {
  color: #e23500;
}

.mobile-web-blocker__preview-exit {
  margin-top: 8px;
  padding: 10px 16px;
  font-size: 0.875rem;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  background: #fafafa;
  color: #444;
  cursor: pointer;
  font-family: inherit;
}

.mobile-web-blocker__preview-exit:active {
  background: #f0f0f0;
}
</style>
